import {Sync, DBView, PropertyPrimitive, BusinessObject, CloudObject, Transaction, Query, ErrorFlow} from 'olympe';
import ValidateExcelConfig from './ValidateExcelConfig';
import XLSX from 'xlsx';

/**
 * Updates the range of cells to start from a new cell or defaults to 'A1' if not specified.
 * @param {string} originalRange
 * @param {string|null|undefined} range
 * @returns {string} Updated range.
 */
export const updateRange = (originalRange, range) => {
    if (range) {
        const [startCell, endCell] = originalRange.split(':');
        const [rangeStart, rangeEnd] = range.split(':');
        const newStartCell = (rangeStart || startCell).toUpperCase();
        const newEndCell = (rangeEnd || endCell).toUpperCase();
        return `${newStartCell}:${newEndCell}`;
    }
    return originalRange;
};

/**
 * Checks if the given cell reference is a valid Excel header or is empty (defaulting to 'A1').
 * @param {string} range
 * @param {boolean}
 */
export const isValidHeader = (range) => {
    if (!range) {
        return true; // Treat null or empty as default 'A1'
    }
    const splitted = range.split(':');
    const regex = /^[A-Za-z]+[0-9]+$/;
    return splitted.every((item) => regex.test(item));
};

/**
 * Extracts data from the Excel sheet based on the specified range.
 * @param {Object[]} excelSheet
 * @param {string} range Excel
 * @param {Object=} params
 * @returns {Object[]}
 */
export const extractDataFromSheet = (excelSheet, range, params = {}) => {
    if (!excelSheet.hasOwnProperty('!ref')) {
        throw new Error(`Provided excelSheet is incorrect`);
    }
    const updatedRange = updateRange(excelSheet['!ref'], range);
    return XLSX.utils.sheet_to_json(excelSheet, {range: updatedRange, ...params});
};

/**
 * Validates input parameters.
 * @param {*} businessObject
 * @param {Map} mapping
 * @param {boolean} automaticDiscovery
 * @param {string} range
 * @throws Error if any parameter is invalid.
 */
export const validateInputs = (businessObject, mapping, automaticDiscovery, range) => {
    if (!(businessObject instanceof Sync)) {
        throw new Error(`The business object is supposed to be an olympe object`);
    }
    if (mapping && !(mapping instanceof Map)) {
        throw new Error(`The mapping is supposed to be a Map object`);
    }
    if (typeof automaticDiscovery !== 'boolean') {
        throw new Error(`The automatic discovery is supposed to be a boolean`);
    }
    if (mapping === undefined && automaticDiscovery === false) {
        throw new Error(`You should either provide a mapping or activate the automatic discovery`);
    }
    if (!isValidHeader(range)) {
        throw new Error(`Invalid range: "${range}"`);
    }
};

/**
 * the xlsx lib provide data as json but the empty cells don't have their property set,
 * So we read the data until all the headers are found. If no values for all lines from the given header,
 * we return that header name as it's probable that there is an issue in the header name
 *
 * @param data
 * @param headers
 * @param expectedLength
 * @return {*}
 */
export const checkHeaders = (data, headers, expectedLength) => {
    let i = 0,
        founds = 0;
    while (i < data.length && founds < expectedLength) {
        for (let j = headers.length - 1; j >= 0; j--) {
            if (data[i][headers[j]] !== undefined) {
                founds++;
                headers.splice(j, 1);
            }
        }
        i++;
    }
    return headers;
};

/**
 * Generate a mapping configuration according to the property names
 *
 * @param businessObject
 */
export const createMappingFromBusinessObject = (businessObject) => {
    const db = DBView.get();
    const mapping = new Map();
    db.getRelated(businessObject, BusinessObject.propertyRel).forEach((prop) => {
        mapping.set(db.name(prop), prop);
    });
    return mapping;
};

/**
 * Create the olympe object in the local DC
 *
 * @param businessObject
 * @param mapping
 * @param line
 * @param transaction
 * @return {CloudObject}
 */
const createOlympeObject = (businessObject, mapping, line, transaction) => {
    const db = DBView.get();
    const properties = new Map();
    for (let [key, prop] of mapping) {
        const type = db.getUniqueRelated(prop, PropertyPrimitive.typeRel);
        if (line[key] !== undefined && line[key] !== null) {
            let value;
            switch (db.name(type)) {
                case 'Number':
                    value = typeof line[key] === 'string' ? Number(line[key].replace(/,/g, '.')) : Number(line[key]);
                    break;
                case 'Date/Time':
                    value = typeof line[key] === 'string' ? new Date(line[key]) : line[key];
                    break;
                case 'String':
                    value = line[key] ? String(line[key]) : '';
                    break;
                default:
                    value = line[key];
            }
            properties.set(prop, value);
        }
    }

    return transaction.create(businessObject, properties);
};

/**
 * Converts data from an Excel sheet into objects based on a specified mapping.
 *
 * @param {Object} excelSheet
 * @param {string} range
 * @param {Map} mapping
 * @param {Sync} businessObject
 * @returns {Array}
 * @throws {ErrorFlow}
 */
export const convertExcelSheetToObjects = async (excelSheet, range, mapping, businessObject) => {
    const excelData = extractDataFromSheet(excelSheet, range);

    const unusedHeaders = checkHeaders(excelData, Array.from(mapping.keys()), Array.from(mapping.keys()).length);

    if (unusedHeaders.length > 0) {
        throw new Error(`No data found for that header: ${unusedHeaders.toString()}`);
    }

    const objects = [];

    const transaction = new Transaction(false);

    excelData.forEach((line) => {
        objects.push(createOlympeObject(businessObject, mapping, line, transaction));
    });

    await transaction.executeAsLarge();
    return objects;
};

/**
 * Check if the provided configuration type is valid.
 * @param {Object} configType
 * @returns {boolean}
 */
export const isValidConfig = (configType) => {
    const configTag = ValidateExcelConfig.asInstance().getTag();
    return configType?.query().followRecursively(CloudObject.extendRel).executeFromCache().has(configTag);
};

/**
 * Load configuration from provided config type.
 * @param {Object} configType
 * @returns {Array}
 */
export const loadConfig = (configType) => {
    const queryRes = Query.instancesOf(configType, false).executeFromCache();
    return queryRes.toArray();
};

/**
 * Validate cells in the workbook or excelSheet against expected cells.
 * @param {Object} workSheets|excelSheet
 * @param {Array} expectedCells
 * @returns {Object}
 */
export const validateCells = (workSheets, expectedCells) => {
    const validationResult = {
        isValid: true,
        message: '',
        wrongSheetNames: new Set(),
        wrongSheetNameStr: '',
    };

    expectedCells.forEach((expectedItem) => {
        let sheet;
        if (workSheets?.Sheets) {
            sheet = workSheets.Sheets[expectedItem.getSheetName()];
        } else if (workSheets?.hasOwnProperty(expectedItem.getSheetName())) {
            sheet = workSheets[expectedItem.getSheetName()];
        }

        if (!sheet) {
            validationResult.isValid = false;
            validationResult.message += `Worksheet "${expectedItem.getSheetName()}" does not exist.\n`;
            validationResult.wrongSheetNames.add(expectedItem.getSheetName());
            return;
        }

        validateItem(expectedItem, sheet, validationResult);
    });

    validationResult.wrongSheetNameStr = Array.from(validationResult.wrongSheetNames).join(', ');
    return validationResult;
};

/**
 * Validate individual item (cell, range, column, row) against expected value.
 * @param {CloudObject} item
 * @param {Object} sheet
 * @param {Object} validationResult
 */
const validateItem = (item, sheet, validationResult) => {
    let isItemValid = true;

    if (/^[a-zA-Z]+$/.test(item.getCell())) {
        // Case when cell consists of one letter
        isItemValid = validateColumn(item, sheet, validationResult);
    } else if (/^\d+$/.test(item.getCell())) {
        // Case when cell consists of one number
        isItemValid = validateRow(item, sheet, validationResult);
    } else if (/^[A-Z]+[0-9]+:[A-Z]+[0-9]+$/.test(item.getCell())) {
        // Case when cell consists of range
        isItemValid = validateRange(item, sheet, validationResult);
    } else {
        isItemValid = validateCell(item, sheet, validationResult);
    }

    if (!isItemValid) {
        validationResult.isValid = false;
    }
};

/**
 * Validate range of cells against expected value.
 * @param {CloudObject} item
 * @param {Object} sheet
 * @param {Object} validationResult
 * @returns {boolean}
 */
const validateRange = (item, sheet, validationResult) => {
    const range = item.getCell();
    const [startCell, endCell] = range.split(':');
    let isValidRange = true;
    let message = '';

    // Convert column letters to a number for easier calculation
    const columnToNumber = (col) => {
        let num = 0;
        for (let i = 0; i < col.length; i++) {
            num = num * 26 + col.charCodeAt(i) - 'A'.charCodeAt(0) + 1;
        }
        return num;
    };

    const startColumn = columnToNumber(startCell.replace(/[\d]/g, ''));
    const startRow = parseInt(startCell.replace(/\D/g, ''), 10);
    const endColumn = columnToNumber(endCell.replace(/[\d]/g, ''));
    const endRow = parseInt(endCell.replace(/\D/g, ''), 10);
    const numColumns = endColumn - startColumn + 1;
    const numRows = endRow - startRow + 1;

    if (numColumns < 0) {
        isValidRange = false;
        message = `Provided range is incorrect: "${range}"`;
    }

    for (let colOffset = 0; colOffset < numColumns; colOffset++) {
        for (let rowOffset = 0; rowOffset < numRows; rowOffset++) {
            const currentColumn = String.fromCharCode('A'.charCodeAt(0) + startColumn + colOffset - 1);
            const currentRow = startRow + rowOffset;
            const cellName = `${currentColumn}${currentRow}`;
            const cellObject = sheet[cellName];
            if (!cellObject || !isCellValueCorrect(cellObject, item.getExpectedValue())) {
                isValidRange = false;
                message += `Error in ${item.getSheetName()}, cell ${cellName} does not match expected value "${item.getExpectedValue()}".\n`;
            }
        }
    }

    if (!isValidRange) {
        validationResult.message += message;
        validationResult.wrongSheetNames.add(item.getSheetName());
    }
    return isValidRange;
};

/**
 * Validate column against expected value.
 * @param {CloudObject} item
 * @param {Object} sheet
 * @param {Object} validationResult
 * @returns {boolean}
 */
const validateColumn = (item, sheet, validationResult) => {
    return (
        Object.entries(sheet).some(([key, value]) => {
            if (key.startsWith(item.getCell()) && !key.startsWith(item.getCell() + item.getCell()[0])) {
                return isCellValueCorrect(value, item.getExpectedValue());
            }
            return false;
        }) || addErrorToValidationResult(item, validationResult, 'column')
    );
};

/**
 * Validate row against expected value.
 * @param {CloudObject} item
 * @param {Object} sheet
 * @param {Object} validationResult
 * @returns {boolean}
 */
const validateRow = (item, sheet, validationResult) => {
    return (
        Object.entries(sheet).some(([key, value]) => {
            if (key.endsWith(item.getCell()) && !key.endsWith(item.getCell() + item.getCell()[0])) {
                return isCellValueCorrect(value, item.getExpectedValue());
            }
            return false;
        }) || addErrorToValidationResult(item, validationResult, 'row')
    );
};

/**
 * Validate individual cell against expected value.
 * @param {CloudObject} item
 * @param {Object} sheet
 * @param {Object} validationResult
 * @returns {boolean}
 */
const validateCell = (item, sheet, validationResult) => {
    const cellObject = sheet[item.getCell()];
    if (!cellObject) {
        return addErrorToValidationResult(item, validationResult, 'cell');
    }

    return isCellValueCorrect(cellObject, item.getExpectedValue()) || addErrorToValidationResult(item, validationResult, 'cell');
};

/**
 * Add error details to validation results.
 * @param {CloudObject} item
 * @param {Object} validationResult
 * @param {string} itemName
 * @returns {boolean}
 */
const addErrorToValidationResult = (item, validationResult, itemName) => {
    validationResult.message += `Error in ${item.getSheetName()}, ${itemName} ${item.getCell()} does not match expected value "${item.getExpectedValue()}".\n`;
    validationResult.wrongSheetNames.add(item.getSheetName());
    return false;
};

/**
 * Check if the cell value matches the expected value.
 * @param {Object} cell
 * @param {string} expectedValue
 * @returns {boolean}
 */
const isCellValueCorrect = (cell, expectedValue) => {
    if (expectedValue.toLowerCase().startsWith('regex:') && expectedValue.endsWith('/')) {
        const pattern = expectedValue.replace(/^[Rr][Ee][Gg][Ee][Xx]:/, '').slice(1, -1);
        const regex = new RegExp(pattern.trim());
        if (cell && cell.v) {
            return regex.test(cell.v.toString());
        }
        return false;
    } else if (cell.t === 'd') {
        return isDate(expectedValue) && new Date(cell.w).getTime() === new Date(expectedValue).getTime();
    } else {
        return cell.v.toString().trim().toUpperCase() === expectedValue.toString().trim().toUpperCase();
    }
};

/**
 * Check if the provided value is a valid date.
 * @param {string} date
 * @returns {boolean}
 */
const isDate = (date) => {
    return new Date(date) !== 'Invalid Date' && !isNaN(new Date(date));
};

/**
 * Create error message based on validation results.
 * @param {Object} validationResults
 * @param {number} errorCode
 * @returns {ErrorFlow}
 */
export const createErrorFlowMessage = (validationResults, errorCode) => {
    return ErrorFlow.create(`Errors found in the following sheets: ${validationResults.wrongSheetNameStr}. Details: ${validationResults.message}`, errorCode);
};
